import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGridLikeFlexChildProps } from '../../components/GridLikeFlex';
import { TextEditorToolbarSection, ToolbarSelectWysiwyg } from '../../styles';
import { useMarkdownEditor } from 'components/WYSIWYG/MarkdownEditor/context';

const leveltoValue = {
  1: 'h1',
  2: 'h2',
  3: 'h3',
  4: 'h4',
  5: 'h5',
  6: 'h6',
  null: 'body',
};

const buildOptions = (t) => [
  { value: 'body', label: t('Body'), level: null },
  { value: 'h1', label: t('Heading 1'), level: 1 },
  { value: 'h2', label: t('Heading 2'), level: 2 },
  { value: 'h3', label: t('Heading 3'), level: 3 },
  { value: 'h4', label: t('Heading 4'), level: 4 },
  { value: 'h5', label: t('Heading 5'), level: 5 },
  { value: 'h6', label: t('Heading 6'), level: 6 },
];

export const HeadingToolbarSection = () => {
  const { t } = useTranslation();
  const { editor, subscribeToSelectionUpdate, subscribeToUpdate } =
    useMarkdownEditor();
  const [ref, props] = useGridLikeFlexChildProps();

  const options = useMemo(() => {
    return buildOptions(t);
  }, [t]);

  const [{ selectedLevel, headingEnabled }, setState] = useState(() => ({
    selectedLevel: null,
    headingEnabled: false,
  }));

  useEffect(() => {
    const handler = ({ editor }) => {
      if (!editor) {
        return;
      }
      const isHeadingActive = editor.isActive('heading');
      const activeLevel = isHeadingActive
        ? editor.getAttributes('heading')?.level
        : null;
      setState({
        selectedLevel: activeLevel,
        headingEnabled:
          editor.can().toggleHeading({ level: 1 }) &&
          !editor.isActive('codeBlock'),
      });
    };

    const selectionUnsubscribe = subscribeToSelectionUpdate(
      'heading-toolbar-section',
      handler
    );
    const updateUnsubscribe = subscribeToUpdate(
      'heading-toolbar-section',
      handler
    );

    return () => {
      selectionUnsubscribe();
      updateUnsubscribe();
    };
  }, [subscribeToSelectionUpdate, subscribeToUpdate]);

  if (!editor) return null;

  return (
    <TextEditorToolbarSection ref={ref} {...props}>
      <ToolbarSelectWysiwyg
        variant="underline"
        options={options}
        value={leveltoValue[selectedLevel]}
        onChange={({ level }) => {
          if (level) {
            editor.chain().focus().setHeading({ level }).run();
          } else {
            editor.chain().focus().setParagraph().run();
          }
        }}
        width={100}
        disabled={!headingEnabled}
      />
    </TextEditorToolbarSection>
  );
};
